/**
 * The workspace's tracker options (`GET /api/desks/tracker-options`): which of
 * Linear and Jira are connected, and what each holds. Add a team reads it to
 * offer the trackers a filter can come from.
 */
import { useEffect, useState } from "react";
import type { TrackerOptionsResponse } from "@yagni/shared";

import { fetchTrackerOptions } from "./teamsApi.js";

/**
 * The workspace's tracker options, read once while `enabled`. A failed read
 * stops there until `retry`: re-reading on every render would hammer a
 * tracker that is down.
 */
export function useTrackerOptions(enabled = true): {
  options: TrackerOptionsResponse | null;
  failed: boolean;
  retry: () => void;
} {
  const [options, setOptions] = useState<TrackerOptionsResponse | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!enabled || options || failed) return;
    let cancelled = false;
    fetchTrackerOptions().then(
      (body) => {
        if (cancelled) return;
        if (body && typeof body === "object" && body.linear && body.jira) setOptions(body);
        else setFailed(true);
      },
      () => {
        if (!cancelled) setFailed(true);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [enabled, options, failed]);
  return { options, failed, retry: () => setFailed(false) };
}
